import React, { Dispatch, SetStateAction, useEffect } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { FaExclamationCircle } from 'react-icons/fa';
import { format } from 'date-fns';

import { UseOutletContextPurchase } from '../Purchase';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { TreasuryAccount } from '../../../../models/treasury.account.model';
import { legendInvalid, legendValid } from '../../../../tools/legend.data.entry.tool';
import { uploadTooltip } from '../../../../tools/tooltip.tool';
import { evaluateLegendValidRequired } from '../../../../scripts/validate.legend.script';
import { expressions } from '../../../../libraries/regular.expressions.library';

import AppLegend from '../../../../components/element/Legend';
import AppInputContact from '../../../../components/element/InputContact';
import AppInputCurrency from '../../../../components/element/InputCurrency';
import AppInputTreasuryAccount from '../../../../components/element/InputTreasuryAccount';
import AppInputPaymentMethod from '../../../../components/element/InputPaymentMethod';

export interface AppPurchaseFormCreateProps {
  purchase: string,
  provider?: {value: string, valid: boolean, object: any},
  concerning: {value: string, valid: boolean},
  date: {value: string, valid: boolean},
  currency: {value: string, valid: boolean, object: any},
  treasuryAccount: {value: string, valid: boolean, object: any},
  paymentMethod?: {value: string, valid: boolean},
  setProvider?: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setConcerning: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setDate: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setCurrency: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setTreasuryAccount: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setPaymentMethod?: Dispatch<SetStateAction<{value: string, valid: boolean}>>
};

const AppPurchaseFormCreate: React.FunctionComponent<AppPurchaseFormCreateProps> = ({purchase, provider, concerning, date, currency, treasuryAccount, paymentMethod, setProvider, setConcerning, setDate, setCurrency, setPaymentMethod, setTreasuryAccount}) => {
  const {userLoggedIn, companyForUser, treasuryAccountsForCompany, contactsForCompany, loadContactsForCompany} = UseOutletContextPurchase()
  const {lang} = StateLanguage()

  const handleChangeConcerning = (event: React.ChangeEvent <HTMLFormElement | HTMLTextAreaElement>) => {
    setConcerning({...concerning, value: event.target.value})
  }

  const handleChangeDate = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    if (event.target.value.length > 0) {
      setDate({value: event.target.value, valid: true})
      legendValid('legend-date-required')
    } else {
      setDate({value: event.target.value, valid: false})
      legendInvalid('legend-date-required')
    }
  }

  const validateConcerning = () => {
    evaluateLegendValidRequired(expressions.text, concerning, setConcerning, 'legend-concerning-valid', 'legend-concerning-required')
  }

  function currentDate(): string {
    return format((new Date()), 'yyyy-MM-dd')
  }

  useEffect( () => {
    setCurrency({value: '', valid: false, object: null})
    setTreasuryAccount({value: '', valid: false, object: null})

    if (provider && setProvider) {
      setProvider({value: '', valid: false, object: null})
    }

    if (companyForUser) {
      setCurrency({value: companyForUser.currency.code, object: companyForUser.currency, valid: true})
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    setTreasuryAccount({value: '', valid: false, object: null})

    if (treasuryAccountsForCompany) {
      if (treasuryAccountsForCompany.some((item) => (item.default))) {
        let defaultTreasuryAccount = treasuryAccountsForCompany.find(item => item.default) as TreasuryAccount
        setTreasuryAccount({value: defaultTreasuryAccount.id, object: defaultTreasuryAccount, valid: true})
      }
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treasuryAccountsForCompany])

  useEffect( () => {
    setDate({value: format((new Date()), 'yyyy-MM-dd'), valid: true})
    uploadTooltip()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      { userLoggedIn && companyForUser && treasuryAccountsForCompany && contactsForCompany &&
        <div className="card card-flush mb-5">
          <div className="card-header">
            <div className="card-title">
              <h2>{lang.labels[`new${purchase !== "expense" ? "Provider" : ""}${purchase.charAt(0).toUpperCase()}${purchase.slice(1)}` as keyof typeof lang.labels]}</h2>
            </div>
          </div>
          <div className="card-body pt-0">
            <div className="fw-bold fs-6 mb-5">{lang.labels[`descriptionCreate${purchase !== "expense" ? "Provider" : ""}${purchase.charAt(0).toUpperCase()}${purchase.slice(1)}` as keyof typeof lang.labels]}</div>
            { provider && setProvider &&
              <div className="row mb-0 mb-lg-2">
                <label className="col-lg-3 col-form-label required">{lang.labels.provider}</label>
                <div className="col-lg-9">
                  <AppInputContact userLoggedIn={userLoggedIn} companyForUser={companyForUser} contactsForCompany={contactsForCompany} contact={provider} setContact={setProvider} loadContactsForCompany={loadContactsForCompany}></AppInputContact>
                </div>
              </div>
            }
            <div className="row mb-0 mb-lg-2">
              <label className="col-lg-3 col-form-label required">{lang.labels.concerning}</label>
              <div className="col-lg-9">
                <TextareaAutosize className="form-control form-control-solid" name="concerning" minRows={2} value={concerning.value} onChange={handleChangeConcerning} onKeyUp={validateConcerning} onBlur={validateConcerning} />
                <AppLegend attribute={{check: concerning.valid, name: "concerning", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
              </div>
            </div>
            <div className="row mb-0 mb-lg-2">
              <label className="col-lg-3 col-form-label">
                <div className="d-flex align-items-center">
                  <span className="required">{lang.labels.creationDate}</span>
                  <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.dateOfDocument}><FaExclamationCircle /></i>
                </div>
              </label>
              <div className="col-lg-9">
                <input className="form-control form-control-solid" type="date" name="date" max={currentDate()} value={date.value} onChange={handleChangeDate} />
                <AppLegend attribute={{check: date.valid, name: "date", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
              </div>
            </div>
            <div className="row mb-0 mb-lg-2">
              <label className="col-lg-3 col-form-label">
                <div className="d-flex align-items-center">
                  <span className="required">{lang.labels.currency}</span>
                  <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.theDefaultCurrencyIsTheCompanys}><FaExclamationCircle /></i>
                </div>
              </label>
              <div className="col-lg-9">
                <AppInputCurrency currency={currency} setCurrency={setCurrency}></AppInputCurrency>
              </div>
            </div>
            <div className={`row mb-0 ${paymentMethod && setPaymentMethod && "mb-lg-2"}`}>
              <label className="col-lg-3 col-form-label">
                <div className="d-flex align-items-center">
                  <span className="required">{lang.labels.treasuryAccount}</span>
                  <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.theDefaultBankAccountIsTheCompanys}><FaExclamationCircle /></i>
                </div>
              </label>
              <div className="col-lg-9">
                <AppInputTreasuryAccount solid={true} treasuryAccountsForCompany={treasuryAccountsForCompany} treasuryAccount={treasuryAccount} setTreasuryAccount={setTreasuryAccount}></AppInputTreasuryAccount>
              </div>
            </div>
            { paymentMethod && setPaymentMethod &&
              <div className="row">
                <label className="col-lg-3 col-form-label required">{lang.labels.paymentMethod}</label>
                <div className="col-lg-9">
                  <AppInputPaymentMethod solid={true} paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod}></AppInputPaymentMethod>
                </div>
              </div>
            }
          </div>
        </div>
      }
    </>
  )
};

export default AppPurchaseFormCreate;
